/**
 * Locale-driven date and number *formatting*, resolved at render time from the
 * app locale (`i18n.language`) — never from strings held in a locale bundle.
 *
 * Field ordering is the whole point: British English reads "13th Jul 2026",
 * American English reads "Jul 13th, 2026". That is a format choice, not copy,
 * so a formatted date must never be baked into a bundle value. Bundles get the
 * surrounding sentence with a `{{date}}` placeholder; this module supplies what
 * goes in the placeholder.
 *
 * Only the ordering differs — month names and ordinal suffixes come from
 * date-fns' default English locale, which is correct for every locale we
 * support, so there is no `date-fns/locale` import to keep in sync.
 */

/**
 * True for American English only. Deliberately not "anything that isn't
 * en-GB": Generic `en` carries the British form (it is what `en-AU` / `en-CA`
 * and unsupported locales fall back to), so day-first is the default and
 * American is the explicit exception.
 */
export function isAmericanEnglish(language: string | undefined): boolean {
  return (language ?? '').toLowerCase().startsWith('en-us');
}

export interface LocaleDatePatterns {
  /** `13th Jul 2026` · `Jul 13th, 2026` */
  ordinalShortMonth: string;
  /** `13th Jul` · `Jul 13th` */
  ordinalShortMonthNoYear: string;
  /** `13th July 2026` · `July 13th, 2026` */
  ordinalLongMonth: string;
  /** `13 Jul 2026` · `Jul 13, 2026` */
  shortMonth: string;
  /** `Mon 13 Jul 2026` · `Mon, Jul 13, 2026` */
  weekdayShortMonth: string;
  /** `Mon 13th Jul` · `Mon Jul 13th` */
  weekdayOrdinalMonth: string;
  /** `Mon 13th Jul 2026` · `Mon Jul 13th, 2026` */
  weekdayOrdinalMonthWithYear: string;
}

const BRITISH_PATTERNS: LocaleDatePatterns = {
  ordinalShortMonth: 'do MMM yyyy',
  ordinalShortMonthNoYear: 'do MMM',
  ordinalLongMonth: 'do MMMM yyyy',
  shortMonth: 'd MMM yyyy',
  weekdayShortMonth: 'EEE d MMM yyyy',
  weekdayOrdinalMonth: 'EEE do MMM',
  weekdayOrdinalMonthWithYear: 'EEE do MMM yyyy',
};

const AMERICAN_PATTERNS: LocaleDatePatterns = {
  ordinalShortMonth: 'MMM do, yyyy',
  ordinalShortMonthNoYear: 'MMM do',
  ordinalLongMonth: 'MMMM do, yyyy',
  shortMonth: 'MMM d, yyyy',
  weekdayShortMonth: 'EEE, MMM d, yyyy',
  weekdayOrdinalMonth: 'EEE MMM do',
  weekdayOrdinalMonthWithYear: 'EEE MMM do, yyyy',
};

/** date-fns patterns for the given app locale. Pass `i18n.language`. */
export function localeDatePatterns(language: string | undefined): LocaleDatePatterns {
  return isAmericanEnglish(language) ? AMERICAN_PATTERNS : BRITISH_PATTERNS;
}

/**
 * Decimal formatter for the app locale.
 *
 * en-GB and en-US happen to group and separate identically, so this changes
 * nothing visible today. It is still the correct call: hard-coding `'en-GB'`
 * says "always British", which stops being true the moment a non-English
 * locale is added, and it silently contradicts the locale the user picked.
 */
export function localeNumberFormat(
  language: string | undefined,
  options?: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  return new Intl.NumberFormat(language ?? 'en-GB', options);
}
